import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi, usersApi, uploadApi, uploadUrl } from '../../api/client';
import styles from './Profile.module.css';

interface Me {
  id: string;
  username: string;
  displayName: string;
  email?: string;
  avatarUrl?: string;
}

export function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, setUserFromToken } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    authApi
      .me()
      .then((data) => {
        if (!cancelled) {
          setMe(data);
          setUsername(data.username);
          setDisplayName(data.displayName);
          setEmail(data.email ?? '');
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load profile.');
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, navigate]);

  const currentAvatarUrl =
    avatarPreview ??
    (me?.avatarUrl
      ? me.avatarUrl.startsWith('http')
        ? me.avatarUrl
        : uploadUrl(me.avatarUrl)
      : null) ??
    null;

  const onAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setAvatarFile(file ?? null);
    if (file) {
      setAvatarPreview(URL.createObjectURL(file));
    } else {
      setAvatarPreview(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let avatarUrl: string | null | undefined = me?.avatarUrl ?? undefined;
      if (avatarFile) {
        const { filename } = await uploadApi.profile(avatarFile);
        avatarUrl = filename;
      }
      await usersApi.updateMe({
        username: username.trim(),
        displayName: displayName.trim(),
        email: email.trim() || undefined,
        ...(avatarUrl !== undefined && { avatarUrl }),
      });
      await setUserFromToken();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  if (loadError) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.error}>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.muted}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1>Profile</h1>
        <p className={styles.subtitle}>Edit your account details.</p>
        <form onSubmit={handleSubmit}>
          <div className={styles.avatarSection}>
            <div className={styles.avatarWrap}>
              {currentAvatarUrl ? (
                <img src={currentAvatarUrl} alt="" className={styles.avatarImg} />
              ) : (
                <span className={styles.avatarPlaceholder}>{displayName.charAt(0) || '?'}</span>
              )}
            </div>
            <label className={styles.avatarLabel}>
              <span className={styles.avatarButton}>Change photo</span>
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={onAvatarChange} className={styles.avatarInput} />
            </label>
          </div>
          <label>
            Username
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              required
            />
          </label>
          <label>
            Display name
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display name"
              autoComplete="name"
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
            />
          </label>
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? 'Saving...' : 'Save changes'}
          </button>
        </form>
      </div>
    </div>
  );
}
